import { useEffect, useRef } from "react";
import WaveCanvas from "@/components/recstudio/WaveCanvas";
import { fmtClock } from "@/lib/studioApi";

/** Scrolling real-time waveform of the microphone while recording (from the engine's live analyser). */
function LiveScroll({ engine, height }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    const w = cv.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    cv.width = w * dpr;
    cv.height = height * dpr;
    const g = cv.getContext("2d");
    g.scale(dpr, dpr);
    const cols = new Float32Array(Math.ceil(w / 3));
    let buf = null;
    let raf = 0;
    const tick = () => {
      const a = engine.recordingAnalyser?.node;
      let peak = 0;
      if (a) {
        buf = buf || new Float32Array(a.fftSize);
        a.getFloatTimeDomainData(buf);
        for (let i = 0; i < buf.length; i += 1) peak = Math.max(peak, Math.abs(buf[i]));
      }
      cols.copyWithin(0, 1);
      cols[cols.length - 1] = peak;
      g.clearRect(0, 0, w, height);
      cols.forEach((v, i) => {
        const h = Math.max(1, Math.min(1, v * 1.4) * (height - 6));
        g.fillStyle = v >= 0.98 ? "#f43f5e" : "#fda4af";
        g.fillRect(i * 3, (height - h) / 2, 2, h);
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [engine, height]);
  return <canvas ref={ref} data-testid="rec-live-waveform" className="block w-full" style={{ height }} />;
}

/** The big waveform: live while recording, otherwise the selected track over the instrumental, click to move the playhead. */
export default function BigWaveform({ engine, phase, buffer, under, duration, playhead, onSeek, onSelect, selection, elapsed, grid }) {
  const live = phase === "recording" || phase === "paused";
  return (
    <div data-testid="rec-big-waveform" data-live={live ? "1" : "0"} className="relative overflow-hidden rounded-3xl border border-white/[0.08] bg-black/40 p-4">
      <div className="mb-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.16em] text-white/50">
        <span className="inline-flex items-center gap-2">
          {phase === "recording" ? <span data-testid="rec-live-dot" className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500 shadow-[0_0_12px_rgba(244,63,94,0.9)]" /> : null}
          {live ? (phase === "paused" ? "Paused" : "Recording — live input") : buffer ? "Your take over the music" : under ? "The music — press Record to sing over it" : "Add an instrumental or just press Record"}
        </span>
        <span data-testid="rec-clock" className="text-white/85">{live ? fmtClock(elapsed) : `${fmtClock(playhead)} / ${fmtClock(duration)}`}</span>
      </div>
      {live ? <LiveScroll engine={engine} height={150} /> : <WaveCanvas testId="rec-static-waveform" buffer={buffer} under={under} duration={duration} playhead={playhead} onSeek={onSeek} onSelect={onSelect} selection={selection} height={150} grid={grid} />}
    </div>
  );
}
